// Bách nghệ P0 (CRAFTING §1–2, DONG_PHU §2–3): linh dược trồng được, đan phương, lửa, công trình.
import type { Phase } from './screens';
import type { HerbAge } from './items/goods';

export interface HerbDef {
  readonly id: string;
  readonly seedId: string;
  /** Giờ thật tới thập niên ở growthMult = 1. */
  readonly matureH: number;
  readonly yield: number;
  readonly phase: Phase;
}

export const HERBS: readonly HerbDef[] = [
  { id: 'herb.dewGrass', seedId: 'seed.dewGrass', matureH: 0.25, yield: 3, phase: 'P0' },
  { id: 'herb.yellowEssence', seedId: 'seed.yellowEssence', matureH: 0.75, yield: 2, phase: 'P0' },
];

export function herbDef(id: string): HerbDef {
  const d = HERBS.find((x) => x.id === id);
  if (!d) throw new RangeError(`unknown herb: ${id}`);
  return d;
}

export function herbBySeed(seedId: string): HerbDef | undefined {
  return HERBS.find((h) => h.seedId === seedId);
}

export type HeatStep = 'gentle' | 'fierce';
export type PillGrade = 'waste' | 'low' | 'mid' | 'high' | 'peak';
export type FireKind = 'mortal' | 'earth';

/** Hiệu ứng đan (tham số ở trung phẩm). */
export type PillEffect =
  | { readonly kind: 'qiBuff'; readonly bucket: number; readonly hours: number }
  | { readonly kind: 'battleHeal'; readonly pct: number }
  | { readonly kind: 'breakthrough'; readonly fromRealm: number };

export interface PillDef {
  readonly id: string;
  readonly toxicity: number;
  readonly effect: PillEffect;
  /** Ghi đè theo phẩm (vd Tụ Khí Đan hạ 0,30 · thượng 0,50 · cực 0,60). */
  readonly gradeBucket?: Partial<Record<Exclude<PillGrade, 'waste'>, number>>;
  readonly phase: Phase;
}

export const PILLS: readonly PillDef[] = [
  {
    id: 'pill.qiGathering',
    toxicity: 4,
    effect: { kind: 'qiBuff', bucket: 0.4, hours: 2 },
    gradeBucket: { low: 0.3, mid: 0.4, high: 0.5, peak: 0.6 },
    phase: 'P0',
  },
  { id: 'pill.springRevival', toxicity: 2, effect: { kind: 'battleHeal', pct: 30 }, phase: 'P0' },
  { id: 'pill.foundation', toxicity: 15, effect: { kind: 'breakthrough', fromRealm: 1 }, phase: 'P0' },
];

export function pillDef(id: string): PillDef | undefined {
  return PILLS.find((p) => p.id === id);
}

export interface RecipeDef {
  readonly id: string;
  readonly outputId: string;
  readonly tier: number;
  readonly minLv: number;
  readonly ingredients: readonly {
    readonly id: string;
    readonly count: number;
    readonly minAge?: HerbAge;
    readonly key?: boolean;
  }[];
  readonly pattern: readonly [HeatStep, HeatStep, HeatStep];
  readonly patternKnown: boolean;
  readonly minFire: FireKind;
  readonly craftMin: number;
  readonly yield: number;
  /** Mốc bước tiểu cảnh được trao đan phương (Trúc Cơ Đan: chạm LK9). */
  readonly grantAtStep?: number;
  readonly phase: Phase;
}

export const RECIPES: readonly RecipeDef[] = [
  {
    id: 'recipe.foundation',
    outputId: 'pill.foundation',
    tier: 1,
    minLv: 1,
    ingredients: [
      { id: 'herb.yellowEssence', count: 2, key: true },
      { id: 'herb.dewGrass', count: 3 },
      { id: 'mat.springWater', count: 1 },
      { id: 'mat.beastCore1', count: 1 },
    ],
    pattern: ['gentle', 'fierce', 'gentle'],
    patternKnown: true,
    minFire: 'mortal',
    craftMin: 10,
    yield: 1,
    grantAtStep: 8,
    phase: 'P0',
  },
];

export function recipeDef(id: string): RecipeDef {
  const d = RECIPES.find((x) => x.id === id);
  if (!d) throw new RangeError(`unknown recipe: ${id}`);
  return d;
}

export function isRecipeId(v: unknown): v is string {
  return typeof v === 'string' && RECIPES.some((r) => r.id === v);
}

export type BuildingId = 'herbGarden' | 'pillRoom';

export interface BuildingDef {
  readonly id: BuildingId;
  readonly baseStone: number;
  readonly materialPerLv: { readonly id: string; readonly perLv: number };
  /** Trần cấp theo chỉ số cảnh giới gốc 0 (DONG_PHU §2). */
  readonly capByRealm: readonly number[];
  readonly phase: Phase;
}

export const BUILDINGS: readonly BuildingDef[] = [
  {
    id: 'herbGarden',
    baseStone: 200,
    materialPerLv: { id: 'mat.spiritWood', perLv: 2 },
    capByRealm: [2, 4, 6, 8, 10, 10, 10, 10, 10],
    phase: 'P0',
  },
  {
    id: 'pillRoom',
    baseStone: 300,
    materialPerLv: { id: 'mat.darkIronSand', perLv: 2 },
    capByRealm: [2, 4, 6, 8, 10, 10, 10, 10, 10],
    phase: 'P0',
  },
];

export function buildingDef(id: BuildingId): BuildingDef {
  const d = BUILDINGS.find((x) => x.id === id);
  if (!d) throw new RangeError(`unknown building: ${id}`);
  return d;
}

/** Số ô Dược Viên: 4 + (cấp − 1) (CRAFTING §1.1). */
export const GARDEN_BASE_PLOTS = 4;
/** Lò theo cấp Đan Phòng: 1; cấp 4 → 2; cấp 8 → 3 (CRAFTING §2.5). */
export const FURNACE_LV_THRESHOLDS = [1, 4, 8] as const;
